import { useState, type ReactNode } from 'react';
import { securityAudit, type SecurityAuditEvent } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { Act, plainWords, useApi } from './common';

type Sev = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'INFO';
const SEVS: Sev[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'INFO'];
// design monVals: SEVC, AUDSEV
const SEVC: Record<Sev, [string, string]> = { CRITICAL: ['#B3261E', 'rgba(179,38,30,.14)'], HIGH: ['#B3261E', 'rgba(179,38,30,.07)'], MEDIUM: ['#264C7E', 'rgba(47,92,151,.12)'], INFO: ['#54606C', 'rgba(34,39,46,.08)'] };
const SEV_NOTE: Record<Sev, [string, string]> = { CRITICAL: ['#B3261E', 'kill switch or abuse lane'], HIGH: ['#B3261E', 'blocked before delivery'], MEDIUM: ['#264C7E', 'held for a human'], INFO: ['#54606C', 'routine, sealed'] };
/** The server's info / warn / critical (and the proposed medium) in the design's four levels. */
export const sevOf = (e: SecurityAuditEvent): Sev => (e.severity === 'critical' ? 'CRITICAL' : e.severity === 'warn' ? 'HIGH' : e.severity === 'medium' ? 'MEDIUM' : 'INFO');
const chip = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:6px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;text-decoration:none;flex:0 0 auto;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;');
const hhmm = (iso: string) => {
  const d = new Date(iso);
  return ('0' + d.getUTCHours()).slice(-2) + ':' + ('0' + d.getUTCMinutes()).slice(-2);
};

/** Truth Ledger > Security audit (design tl.off, ledIsAudit): security events, expandable. */
export function SecurityAudit({ tabs }: { tabs: ReactNode }) {
  const [t, setT] = useState<'ALL' | Sev>('ALL');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const audit = useApi((c, signal) => securityAudit(c, { query: { limit: 100 }, signal }), [], { pollMs: 30000 });
  const all = audit.data?.events ?? [];
  const term = q.trim().toLowerCase();
  const rows = all.filter((r) => (t === 'ALL' || sevOf(r) === t) && (!term || [r.event ?? r.type, r.description, r.actor].join(' ').toLowerCase().includes(term)));

  return (
    <>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 18px;flex-wrap:wrap;min-width:0;')}>
        {tabs}
        {SEVS.map((s) => (
          <span key={s} title={SEV_NOTE[s][1]} style={sx('display:inline-flex;align-items:center;gap:6px;white-space:nowrap;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:' + SEV_NOTE[s][0] + ';')} />
            <span style={sx('font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;color:#22272E;')}>{audit.data ? String(all.filter((a) => sevOf(a) === s).length) : '–'}</span>
            <span style={sx('font-size:16px;color:#3C454F;')}>{s}</span>
          </span>
        ))}
        <span title="Security events are sealed to the tenant chain" style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;white-space:nowrap;')}>chain</span>
      </div>
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:12px;flex-wrap:nowrap;white-space:nowrap;min-width:0;border-bottom:1px solid rgba(34,39,46,.12);padding-bottom:8px;')}>
        <span role="group" aria-label="Severity" style={sx('flex:1 1 auto;min-width:0;display:flex;align-items:center;gap:6px;overflow-x:auto;scrollbar-width:none;')}>
          {(['ALL', ...SEVS] as const).map((n) => (
            <Act
              key={n}
              pressed={t === n}
              css={chip(t === n)}
              onClick={() => {
                setT(n);
                setOpen(null);
              }}
            >
              {n}
            </Act>
          ))}
        </span>
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(null);
          }}
          aria-label="Search actor or event"
          placeholder="Search actor or event…"
          style={sx('flex:0 1 200px;min-width:120px;min-height:34px;width:200px;padding:0 11px;border:1px solid rgba(34,39,46,.16);border-radius:9px;background:#FCFAF4;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;')}
        />
      </div>
      <div style={sx('flex:1 1 auto;min-height:0;overflow:auto;min-width:0;padding-right:3px;')}>
        <div style={sx('min-width:620px;')}>
          <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding-bottom:6px;')}>
            <span style={sx('font-size:16px;font-weight:800;color:#22272E;letter-spacing:-.01em;white-space:nowrap;')}>Security events</span>
            <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>click a row to expand its metadata · {rows.length} shown</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:62px 100px 168px minmax(0,1fr) 128px;gap:10px;padding:0 0 6px;border-bottom:1px solid rgba(34,39,46,.12);')}>
            {['Time', 'Severity', 'Event type', 'Summary', 'Actor'].map((c) => (
              <span key={c} style={sx('font-size:14px;font-weight:700;letter-spacing:.06em;color:#54606C;white-space:nowrap;')}>
                {c}
              </span>
            ))}
          </div>
          {rows.map((r) => {
            const s = sevOf(r);
            const isOpen = open === r.id;
            return (
              <div key={r.id}>
                <Act
                  pressed={isOpen}
                  onClick={() => setOpen(isOpen ? null : r.id)}
                  css={'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);display:grid;grid-template-columns:62px 100px 168px minmax(0,1fr) 128px;gap:10px;align-items:center;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.07);text-decoration:none;color:inherit;width:100%;' + (isOpen ? 'background:rgba(79,138,214,.08);' : '')}
                >
                  <span style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>{hhmm(r.timestamp)}</span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.05em;border-radius:5px;padding:2px 6px;text-align:center;white-space:nowrap;color:' + SEVC[s][0] + ';background:' + SEVC[s][1] + ';')}>{s}</span>
                  <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{r.event ?? r.type}</span>
                  <span style={sx('font-size:16px;color:#3C454F;line-height:1.35;min-width:0;')}>{plainWords(r.description)}</span>
                  <span style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>{plainWords(r.actor) || 'Aithora'}</span>
                </Act>
                {isOpen && <div style={sx("white-space:pre-wrap;font-variant-numeric:tabular-nums;font-size:14px;color:#3C454F;background:rgba(34,39,46,.04);border-radius:8px;padding:10px 12px;margin:6px 0 8px;")}>{JSON.stringify(r, null, 2)}</div>}
              </div>
            );
          })}
          {audit.data && rows.length === 0 && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>{term ? 'No security event matches "' + q.trim() + '".' : 'No events of that type in this window.'}</div>}
          {audit.error && <div style={sx('font-size:16px;color:#54606C;padding:16px 2px;')}>{audit.error.message}</div>}
        </div>
      </div>
    </>
  );
}
